import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface SmoothTransitionProps {
  children: React.ReactNode;
  show: boolean;
  duration?: number;
  className?: string;
  type?: 'fade' | 'slide-up' | 'slide-down' | 'scale' | 'slide-left' | 'slide-right';
  delay?: number;
}

const SmoothTransition: React.FC<SmoothTransitionProps> = ({
  children,
  show,
  duration = 300,
  className,
  type = 'fade',
  delay = 0
}) => {
  const [shouldRender, setShouldRender] = useState(show);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setShouldRender(true);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, delay);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration, delay]);

  if (!shouldRender) return null;

  const transitionClasses = {
    fade: {
      base: 'transition-opacity ease-in-out',
      show: 'opacity-100',
      hide: 'opacity-0'
    },
    'slide-up': {
      base: 'transition-all ease-in-out transform',
      show: 'opacity-100 translate-y-0',
      hide: 'opacity-0 translate-y-4'
    },
    'slide-down': {
      base: 'transition-all ease-in-out transform',
      show: 'opacity-100 translate-y-0',
      hide: 'opacity-0 -translate-y-4'
    },
    'slide-left': {
      base: 'transition-all ease-in-out transform',
      show: 'opacity-100 translate-x-0',
      hide: 'opacity-0 translate-x-4'
    },
    'slide-right': {
      base: 'transition-all ease-in-out transform',
      show: 'opacity-100 translate-x-0',
      hide: 'opacity-0 -translate-x-4'
    },
    scale: {
      base: 'transition-all ease-in-out transform',
      show: 'opacity-100 scale-100',
      hide: 'opacity-0 scale-95'
    }
  };

  const transition = transitionClasses[type];

  return (
    <div
      className={cn(
        transition.base,
        isVisible ? transition.show : transition.hide,
        className
      )}
      style={{ transitionDuration: `${duration}ms` }}
    >
      {children}
    </div>
  );
};

// Stagger animation for lists
interface StaggeredListProps {
  children: React.ReactNode[];
  show: boolean;
  staggerDelay?: number;
  className?: string;
}

export const StaggeredList: React.FC<StaggeredListProps> = ({
  children,
  show,
  staggerDelay = 100,
  className
}) => {
  return (
    <div className={className}>
      {children.map((child, index) => (
        <SmoothTransition
          key={index}
          show={show}
          type="slide-up"
          delay={index * staggerDelay}
          duration={300}
        >
          {child}
        </SmoothTransition>
      ))}
    </div>
  );
};

export default SmoothTransition;